import {
  Outlet,
  createFileRoute,
  redirect,
  useMatches,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { CapturePointShell } from '@paige/capturepoint';
import { TenantProvider, useTenant } from '../tenant/TenantContext';
import { useLiveUpdates } from '../live/useLiveUpdates';
import { homeFor, rememberCompany, requireSignIn, slugAddress } from '../auth/gate';
import { usePrincipal } from '../auth/AuthBoundary';
import { ShellUtilities } from '../shell/ShellUtilities';
import { CreditBanner } from '../shell/CreditBanner';
import { SupportViewBanner } from '../shell/SupportViewBanner';
import { ReadOnlyRefusals } from '../shell/ReadOnlyRefusals';
import { grantFor } from '../shell/support-view';
import { isAdmin, seesBilling } from '../tenant/role';
import { AccountBanner } from '../shell/AccountBanner';
import { useAccountBanner } from '../shell/account-banner-shown';
import { tenantItems } from '../shell/tenant-items';
import { inShell } from '../shell/in-shell';

export const Route = createFileRoute('/t/$tenant')({
  beforeLoad: async ({ context, location, params, preload }) => {
    const principal = await requireSignIn(context.auth, location.href);
    const member = principal.tenants.some((t) => t.id === params.tenant);
    // A link that names the company by its id (a notice or an email written before the links said
    // the slug) opens the same page under the slug, path, query and hash kept; it used to be sent
    // to the jobs home (first-run walk, 2026-10-06).
    const bySlug = member ? null : slugAddress(principal, params.tenant, location);
    if (bySlug) throw redirect(bySlug);
    if (!member && !principal.staff) throw redirect(homeFor(principal));
    // A preload is the router reading ahead, not the person opening a company: the one they left
    // is preloaded a moment after they switch, and remembering it would undo the switch.
    if (member && !preload) rememberCompany(params.tenant);
  },
  component: TenantLayout,
});

function TenantLayout(): ReactNode {
  const { tenant } = Route.useParams();
  return (
    <TenantProvider tenantId={tenant}>
      <TenantShell />
    </TenantProvider>
  );
}

/**
 * CapturePoint's shell around the tenant's screens: Jobs, Inbox, Settings, and Admin for staff. The
 * credits banner sits beside the screens, never in them: a fixed line that moves nothing.
 */
function TenantShell(): ReactNode {
  const tenant = useTenant();
  const principal = usePrincipal();
  const navigate = useNavigate();
  useLiveUpdates(tenant.id);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The frame follows the matches the router has committed, not the address it is heading for:
  // a screen is never re-parented into or out of the shell while the next one loads
  // (IMPLEMENTATION.md, sell-ready sprint 2, row 13).
  const framed = useMatches({ select: (matches) => matches.some((m) => inShell(m.routeId)) });
  const go = (to: string) => () => void navigate({ to });
  const items = tenantItems({
    tenantId: tenant.id,
    pathname,
    staff: principal.staff,
    go,
  });
  const banner = <Banners tenantId={tenant.id} />;
  if (!framed)
    return (
      <>
        <Outlet />
        {banner}
      </>
    );
  return (
    <>
      <CapturePointShell
        items={items}
        onBrand={go(`/t/${tenant.id}`)}
        utilities={<ShellUtilities tenantId={tenant.id} />}
      >
        <Outlet />
      </CapturePointShell>
      {banner}
    </>
  );
}

/**
 * The lines beside the screens: the account's one problem, else credits (one notice at a time; the account's
 * explains why pages stopped), and for staff inside a company they are not in, the support view.
 */
function Banners({ tenantId }: { tenantId: string }): ReactNode {
  const principal = usePrincipal();
  const member = principal.tenants.some((t) => t.id === tenantId);
  const account = useAccountBanner(tenantId);
  return (
    <>
      {account.notice ? (
        <AccountBanner
          tenantId={tenantId}
          notice={account.notice}
          acts={isAdmin(principal, tenantId)}
          onClose={account.close}
        />
      ) : (
        <CreditBanner tenantId={tenantId} seesBilling={seesBilling(principal, tenantId)} />
      )}
      {principal.staff && (!member || grantFor(tenantId)) ? (
        <>
          <SupportViewBanner tenantId={tenantId} />
          <ReadOnlyRefusals />
        </>
      ) : null}
    </>
  );
}
